<template>
    <app-layout title="Edit Recurring Order">
        <form @submit.prevent="handleSubmit">
            <page-header>
                <template #title>Edit Recurring Order</template>

                <template #actions>
                    <jet-button v-show="form.isDirty" type="submit" class="leading-none" :disabled="form.processing" >
                        Save
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        class="leading-none"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="backUrl"
                        class="leading-none"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <customer-form
                :customers="customers"
                :errors="errors"
                :form="form"
                :is-admin="isAdmin"
                :active-customer="activeCustomer"
                class="px-3 md:px-4 lg:px-8"
            />

            <div class="px-3 md:px-4 lg:px-8 py-4">
                <order-items-edit
                    :form="form"
                    :customers="customers"
                />
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />
    </app-layout>
</template>

<script>
import { useForm, usePage } from '@inertiajs/inertia-vue3';
import { watch, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import UseUserAuth from '@/Composables/UseUserAuth'
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import CustomerForm from '@/Pages/RecurringOrders/Partials/CustomerForm.vue';
import OrderItemsEdit from '@/Pages/RecurringOrders/Partials/OrderItemsEdit';
import JetButton from '@/Jetstream/Button';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';

export default {
    setup(props) {
        const { isAdmin } = UseUserAuth(usePage().props.value.user)
        const activeCustomer = ref(usePage().props.value.activeCustomer || null);

        const form = useForm({
            id: props.recurring_order.id,
            customer_id: props.recurring_order.customer_id,
            shipping_method: props.recurring_order.shipping_method ?? 'delivery',
            schedule_days: props.recurring_order.schedule_days.split(','),
            schedule_frequency: props.recurring_order.schedule_frequency,
            schedule_units: props.recurring_order.schedule_units,
            schedule_ordinance: props.recurring_order.schedule_ordinance?.split(',') ?? [], // empty array if null
            weeks_in_advance: props.recurring_order.weeks_in_advance,
            order_items: props.recurring_order.order_items.map(i => ({
                id: i.id,
                product_id: i.product_id,
                quantity: i.quantity,
                price: i.price ?? i.product.price,
                notes: i.notes,
                product: i.product
            })),
            override_delivery_fee: !!props.recurring_order.delivery_fee,
            delivery_fee: props.recurring_order.delivery_fee ?? null,
            next_generation_date: props.recurring_order.next_generation_date ?? null,
        })

        //  If customer switches activeCustomer while editing, sends them back to the index page
        watch(
            () => usePage().props.value.activeCustomer,
            (newVal, oldVal) => {
                if (newVal && oldVal && newVal.id !== oldVal.id) {
                    Inertia.get(route('recurring_orders.index'))
                }
            }
        )

        return { form, isAdmin, activeCustomer }
    },
    

    components: {
        AppLayout,
        PageHeader,
        CustomerForm,
        OrderItemsEdit,
        JetButton,
        JetTertiaryButton,
        UnsavedChangesModal2,
    },

    props: [
        'recurring_order',
        'customers',
        'errors',
    ],

    data() {
        return {
            formRoute: this.route('recurring_orders.update', this.recurring_order.id),
            showUnsavedChangesModal: false,
        }
    },

    methods: {
        handleSubmit () {
            if(!this.form.processing) {
                const returnUrl = route().params.return;

                this.form
                    .transform((data) => ({
                        ...data,
                        schedule_days: data.schedule_days,
                        order_items: data.order_items.map(({ product, customer_price, customer_split_case_price, last_quantity_for_price, ...item }) => item),
                    }))
                    .submit('put', route('recurring_orders.update', {
                        recurring_order: this.recurring_order.id,
                        return: returnUrl,
                    }));
            }
        },
    },

    computed: {
        backUrl () {
            const params = route().params;

            if (params.return) {
                return params.return;
            }

            return route('recurring_orders.index', {
                page: this.$page.props.page.recurringOrders,
            });
        },

        activeCustomer() {
            return this.$page.props.activeCustomer;
        }
    },
}
</script>

<style>

</style>
